import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SolicitudCard from "./SolicitudCard";
import Paginacion from "../shared/Paginacion";
import { useToast } from "../ui/ToastContext";
import {
  listarSolicitudes as apiListarSolicitudes,
} from "../../services/solicitudes";

// ====== Utils texto ======
const strip = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

// ====== Utils fecha/hora ======
function normalizeHora(hora) {
  const [h = "00", m = "00", s = "00"] = String(hora || "").trim().split(":");
  const pad2 = (x) => String(x ?? "00").padStart(2, "0");
  return [pad2(h), pad2(m), pad2(s)].join(":");
}
function parseFechaSolo(fechaStr) {
  if (!fechaStr) return null;
  const raw = String(fechaStr).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [d, m, y] = raw.split("/").map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(raw);
  return isNaN(dt.getTime()) ? null : dt;
}
function toDate(fechaStr, horaStr) {
  const base = parseFechaSolo(fechaStr);
  if (!base) return null;
  const [hh, mm, ss] = normalizeHora(horaStr).split(":").map((n) => Number(n));
  base.setHours(hh, mm, ss, 0);
  return base;
}
function getTimestamp(s) {
  if (s?.timestamp) {
    const dt = new Date(s.timestamp);
    if (!isNaN(dt.getTime())) return dt;
  }
  const dt2 = toDate(s?.fecha, s?.hora);
  return dt2 && !isNaN(dt2.getTime()) ? dt2 : null;
}
function msLimite(rango) {
  switch (rango) {
    case "24h": return 24 * 60 * 60 * 1000;
    case "5d":  return 5  * 24 * 60 * 60 * 1000;
    case "7d":  return 7  * 24 * 60 * 60 * 1000;
    case "14d": return 14 * 24 * 60 * 60 * 1000;
    default:    return 0;
  }
}
function fmt(iso) {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(iso)) return iso;
  return iso;
}

// ====== Estado desde URL/KPI ======
const normalizeEstado = (v) => {
  const s = strip(v);
  if (!s) return null;
  if (["todas", "todos", "all", "*"].includes(s)) return "todas";
  if (["pendiente", "pendientes"].includes(s)) return "pendiente";
  if (["aprobada", "aprobadas"].includes(s)) return "aprobada";
  if (["rechazada", "rechazadas", "denegada", "denegadas"].includes(s)) return "rechazada";
  return null;
};
function estadoFromLocation(location) {
  try {
    const params = new URLSearchParams(location.search);
    const q = params.get("estado");
    const n = normalizeEstado(q);
    if (n) return n;
  } catch {}
  if (location.hash && location.hash.includes("estado=")) {
    try {
      const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
      const hp = new URLSearchParams(hash.includes("?") ? hash.split("?")[1] : hash);
      const n = normalizeEstado(hp.get("estado"));
      if (n) return n;
    } catch {}
  }
  const parts = location.pathname.split("/").filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const n = normalizeEstado(parts[i]);
    if (n) return n;
  }
  return null;
}
function pickFiltroEstado({ location, filtroInicial, estadoInicial }) {
  const p1 = normalizeEstado(filtroInicial?.estado);
  if (p1) return p1;
  const p2 = normalizeEstado(estadoInicial);
  const p3 = estadoFromLocation(location);
  return p3 || p2 || "pendiente";
}

const POR_PAGINA_DEFAULT = 8;

export default function SolicitudesTab({
  // 🔁 Fallback local (si no usamos servidor)
  solicitudes = [],
  onResolver,
  filtroInicial,                 // { estado, solo_pendientes? }
  estadoInicial = "pendiente",
  rangoInicial = "todos",

  // ✅ Server mode (nuevo backend). Si pasas `token`, esta vista hace fetch server-side.
  token = null,
}) {
  const { show } = useToast();
  const location = useLocation();

  // ====== Filtros/UI ======
  const [filtroEstado, setFiltroEstado] = useState(() =>
    pickFiltroEstado({ location, filtroInicial, estadoInicial })
  );
  useEffect(() => {
    const next = pickFiltroEstado({ location, filtroInicial, estadoInicial });
    if (next !== filtroEstado) {
      setFiltroEstado(next);
      setPagina(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, filtroInicial?.estado, estadoInicial]);

  const [busqueda, setBusqueda] = useState("");
  const [rangoSeleccionado, setRangoSeleccionado] = useState(rangoInicial);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_DEFAULT);
  const [fadeOn, setFadeOn] = useState(true);
  const tokens = useMemo(() => strip(busqueda).split(/\s+/).filter(Boolean), [busqueda]);

  useEffect(() => {
    setFadeOn(false);
    const t = setTimeout(() => setFadeOn(true), 0);
    return () => clearTimeout(t);
  }, [filtroEstado, rangoSeleccionado, fechaInicio, fechaFin, busqueda, porPagina, pagina]);

  const hasActiveFilters =
    !!busqueda ||
    rangoSeleccionado === "personalizado" ||
    (rangoSeleccionado !== "todos" && ["24h", "5d", "7d", "14d"].includes(rangoSeleccionado)) ||
    !!fechaInicio ||
    !!fechaFin;

  const clearFilters = useCallback(() => {
    setBusqueda("");
    setRangoSeleccionado("todos");
    setFechaInicio("");
    setFechaFin("");
    setPagina(1);
    show("Filtros limpiados");
  }, [show]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && hasActiveFilters) {
        e.preventDefault();
        clearFilters();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [hasActiveFilters, clearFilters]);

  // ====== SERVER MODE ======
  const serverEnabled = !!token;

  const [srvItems, setSrvItems] = useState([]);
  const [srvTotal, setSrvTotal] = useState(0);
  const [srvLoading, setSrvLoading] = useState(false);

  // Deriva params para backend
  const deriveServerParams = useCallback(() => {
    // estado/solo_pendientes
    const estado = filtroEstado && filtroEstado !== "todas" ? filtroEstado : undefined;
    const solo_pendientes = filtroEstado === "pendiente";

    // fecha desde/hasta
    let desde, hasta;
    const now = new Date();
    if (rangoSeleccionado === "personalizado") {
      if (fechaInicio) desde = fechaInicio;
      if (fechaFin) hasta = fechaFin;
    } else if (["24h", "5d", "7d", "14d"].includes(rangoSeleccionado)) {
      const ms = msLimite(rangoSeleccionado);
      const ini = new Date(now.getTime() - ms);
      const pad = (n) => String(n).padStart(2, "0");
      desde = `${ini.getFullYear()}-${pad(ini.getMonth() + 1)}-${pad(ini.getDate())}`;
      hasta = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    }

    return {
      usuario: undefined, // admin/manager verán todas; empleado, el backend fuerza el suyo
      estado: solo_pendientes ? undefined : estado,
      tipo: undefined,
      desde,
      hasta,
      q: busqueda || undefined,
      page: pagina,
      per_page: porPagina,
      order_by: "timestamp",
      order_dir: "desc",
      solo_pendientes,
    };
  }, [filtroEstado, rangoSeleccionado, fechaInicio, fechaFin, busqueda, pagina, porPagina]);

  // Fetch server-side cuando cambian filtros o paginación
  useEffect(() => {
    if (!serverEnabled) return;

    const run = async () => {
      try {
        setSrvLoading(true);
        const params = deriveServerParams();
        const res = await apiListarSolicitudes(token, params);
        setSrvItems(res.items || []);
        setSrvTotal(Number(res.total || 0));
      } catch (err) {
        setSrvItems([]);
        setSrvTotal(0);
        console.error("❌ Error listando solicitudes (server):", err);
      } finally {
        setSrvLoading(false);
      }
    };
    run();
  }, [serverEnabled, token, deriveServerParams]);

  // ====== CLIENT MODE (fallback: filtra local) ======
  const filtradasLocal = useMemo(() => {
    if (serverEnabled) return []; // no se usa
    const now = Date.now();
    const limite = msLimite(rangoSeleccionado);

    let inicioPers = null;
    let finPers = null;
    if (rangoSeleccionado === "personalizado" && (fechaInicio || fechaFin)) {
      if (fechaInicio) inicioPers = parseFechaSolo(fechaInicio);
      if (fechaFin) {
        finPers = parseFechaSolo(fechaFin);
        if (finPers) finPers.setHours(23, 59, 59, 999);
      }
    }

    return (solicitudes || [])
      .filter((s) => {
        if (filtroEstado !== "todas" && s.estado !== filtroEstado) return false;

        const ts = getTimestamp(s);
        if (!ts) return false;
        if (limite > 0 && now - ts.getTime() > limite) return false;
        if (rangoSeleccionado === "personalizado") {
          if (inicioPers && ts < inicioPers) return false;
          if (finPers && ts > finPers) return false;
        }

        const tokens = strip(busqueda).split(/\s+/).filter(Boolean);
        if (tokens.length > 0) {
          const haystack = strip([s.usuario_email, s.tipo, s.motivo].join(" "));
          if (!tokens.every((t) => haystack.includes(t))) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const ta = getTimestamp(a)?.getTime() ?? 0;
        const tb = getTimestamp(b)?.getTime() ?? 0;
        return tb - ta;
      });
  }, [serverEnabled, solicitudes, filtroEstado, rangoSeleccionado, busqueda, fechaInicio, fechaFin]);

  // ====== Datos a pintar (server vs local) ======
  const totalItems = serverEnabled ? srvTotal : filtradasLocal.length;
  const totalPaginas = Math.max(1, Math.ceil(totalItems / porPagina));

  const itemsPagina = useMemo(() => {
    if (serverEnabled) return srvItems;
    const desde = (pagina - 1) * porPagina;
    const hasta = Math.min(desde + porPagina, filtradasLocal.length);
    return filtradasLocal.slice(desde, hasta);
  }, [serverEnabled, srvItems, filtradasLocal, pagina, porPagina]);

  const desdeLabel = serverEnabled
    ? (totalItems ? (pagina - 1) * porPagina + 1 : 0)
    : (filtradasLocal.length ? (pagina - 1) * porPagina + 1 : 0);

  const hastaLabel = serverEnabled
    ? Math.min(pagina * porPagina, totalItems)
    : Math.min((pagina - 1) * porPagina + itemsPagina.length, filtradasLocal.length);

  // ====== Labels ======
  const estadoLabel =
    filtroEstado === "pendiente" ? "Pendientes" :
    filtroEstado === "aprobada"  ? "Aprobadas" :
    filtroEstado === "rechazada" ? "Rechazadas" : "Todas";

  const rangoLabel =
    rangoSeleccionado === "24h" ? "Últimas 24h" :
    rangoSeleccionado === "5d"  ? "Últimos 5 días" :
    rangoSeleccionado === "7d"  ? "Últimos 7 días" :
    rangoSeleccionado === "14d" ? "Últimos 14 días" :
    rangoSeleccionado === "personalizado"
      ? (fechaInicio && fechaFin
          ? `Personalizado: ${fmt(fechaInicio)} → ${fmt(fechaFin)}`
          : fechaInicio ? `Personalizado: desde ${fmt(fechaInicio)}`
          : fechaFin ? `Personalizado: hasta ${fmt(fechaFin)}`
          : "Personalizado")
      : "Todo";

  const microcopy = `Mostrando: ${estadoLabel} · ${rangoLabel} · ${totalItems} resultado${totalItems === 1 ? "" : "s"}${busqueda ? ` · Filtro: “${busqueda}”` : ""}`;

  // ====== Render ======
  return (
    <div className="space-y-5">
      {/* TOOLBAR */}
      <div className="sticky top-20 z-10">
        <div className="rounded-2xl border border-white/50 bg-white/50 backdrop-blur-md shadow-sm px-3 sm:px-4 pt-2 pb-3">
          <p className="text-xs sm:text-sm text-gray-700 flex items-center gap-2 mb-2">
            <span aria-hidden>📊</span>
            <span aria-live="polite">
              {microcopy}{serverEnabled && (srvLoading ? " · cargando…" : "")}
            </span>
          </p>

          {/* FILTROS */}
          <div className="flex flex-col gap-3">
            {/* Buscador */}
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 select-none">🔍</span>
              <input
                type="text"
                placeholder="Buscar por usuario, tipo o motivo…"
                aria-label="Buscar solicitudes"
                value={busqueda}
                onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
                className="w-full pl-10 pr-3 py-2 rounded-xl border border-white/60 bg-white/70 backdrop-blur focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Rango rápido en fila desplazable (mejor móvil) */}
            <div className="relative">
              {/* sutiles desvanecidos laterales */}
              <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white/70 to-transparent rounded-l-xl" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white/70 to-transparent rounded-r-xl" />
              <div
                className="flex items-center gap-2 overflow-x-auto scroll-smooth scrollbar-thin pr-1"
                style={{ WebkitOverflowScrolling: "touch" }}
                aria-label="Filtros rápidos de rango temporal"
              >
                {[
                  { id: "24h",  label: "24h" },
                  { id: "5d",   label: "5 días" },
                  { id: "7d",   label: "7 días" },
                  { id: "14d",  label: "14 días" },
                  { id: "todos",label: "Todo" },
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => { setRangoSeleccionado(id); setPagina(1); }}
                    className={`px-3 py-1.5 rounded-xl text-sm border whitespace-nowrap transition ${
                      rangoSeleccionado === id
                        ? "bg-blue-600 text-white border-blue-600 shadow"
                        : "bg-white/60 text-gray-800 border-white/60 hover:bg-white/80"
                    }`}
                    title={`Rango: ${label}`}
                  >
                    {label}
                  </button>
                ))}

                {/* Personalizado (detalles colapsables) */}
                <details className="group">
                  <summary
                    className={`list-none cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm border transition ${
                      rangoSeleccionado === "personalizado"
                        ? "bg-blue-600 text-white border-blue-600 shadow"
                        : "bg-white/60 text-gray-800 border-white/60 hover:bg-white/80"
                    }`}
                    onClick={(e) => {
                      const el = e.currentTarget.parentElement;
                      if (!el.open) { setRangoSeleccionado("personalizado"); setPagina(1); }
                    }}
                  >
                    <span>Personalizado</span>
                    <span className="transition group-open:rotate-180">▾</span>
                  </summary>

                  <div className="mt-2 flex items-center gap-2 p-2 rounded-xl border border-white/60 bg-white/70 backdrop-blur">
                    <input
                      type="date"
                      value={fechaInicio}
                      onChange={(e) => { setFechaInicio(e.target.value); setPagina(1); }}
                      className="px-2 py-1.5 rounded-lg border border-white/60 bg-white/80"
                      aria-label="Fecha inicio"
                    />
                    <span className="text-gray-500">→</span>
                    <input
                      type="date"
                      value={fechaFin}
                      onChange={(e) => { setFechaFin(e.target.value); setPagina(1); }}
                      className="px-2 py-1.5 rounded-lg border border-white/60 bg-white/80"
                      aria-label="Fecha fin"
                    />
                  </div>
                </details>

                {/* Limpiar filtros */}
                <button
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  className={`ml-1 px-3 py-1.5 rounded-xl text-sm border transition ${
                    hasActiveFilters
                      ? "bg-white/60 text-gray-800 border-white/60 hover:bg-white/80"
                      : "bg-white/40 text-gray-400 border-white/40 cursor-not-allowed"
                  }`}
                  title="Borrar búsqueda y filtros"
                >
                  Limpiar
                </button>

                {/* Por página (compacto en móvil) */}
                <label className="ml-1 flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
                  <span className="hidden sm:inline">Por página</span>
                  <select
                    aria-label="Resultados por página"
                    value={porPagina}
                    onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }}
                    className="px-2 py-1.5 rounded-lg border border-white/60 bg-white/70"
                  >
                    <option value={4}>4</option>
                    <option value={8}>8</option>
                    <option value={12}>12</option>
                  </select>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LISTADO */}
      <ul
        className="list-none p-0 m-0 space-y-4"
        style={{
          opacity: fadeOn ? 1 : 0,
          transform: fadeOn ? "translateY(0px)" : "translateY(4px)",
          transition: "opacity 200ms ease, transform 200ms ease",
        }}
      >
        {itemsPagina.length === 0 ? (
          <li className="text-gray-600 italic text-center mt-6">
            {srvLoading ? "Cargando…" : "No se encontraron solicitudes."}
          </li>
        ) : (
          itemsPagina.map((s) => (
            <li key={s.id}>
              <SolicitudCard
                solicitud={s}
                onResolver={async (id, aprobar) => {
                  if (typeof onResolver === "function") {
                    await onResolver(id, aprobar);
                    if (serverEnabled) {
                      try {
                        const params = deriveServerParams();
                        const res = await apiListarSolicitudes(token, params);
                        setSrvItems(res.items || []);
                        setSrvTotal(Number(res.total || 0));
                      } catch (e) {
                        console.error("❌ refresh tras resolver:", e);
                      }
                    }
                  }
                }}
                highlight={tokens}
              />
            </li>
          ))
        )}
      </ul>

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
          <span className="text-sm text-gray-700">
            Mostrando <strong>{desdeLabel}</strong>–<strong>{hastaLabel}</strong> de <strong>{totalItems}</strong>
          </span>

          <Paginacion
            pagina={pagina}
            totalPaginas={totalPaginas}
            onAnterior={() => setPagina((p) => Math.max(1, p - 1))}
            onSiguiente={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
            totalItems={totalItems}
            porPagina={porPagina}
            onGoToPage={(n) => setPagina(n)}
          />
        </div>
      )}
    </div>
  );
}
